// AddGradeDialog component

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert
} from '@mui/material';
import { createGrade } from '../services/gradeService';
import { getStudents } from '../services/studentService';
import { getCourses } from '../services/courseService';
import { getExams } from '../services/examService';
import { Grade, Student, Course, Exam } from '../types';
import { useAuth } from '../context/AuthContext';

interface AddGradeDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddGradeDialog: React.FC<AddGradeDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    student_id: '',
    course_id: '',
    assignment_name: '',
    grade: '',
    max_grade: '',
    grade_type: 'assignment',
    comments: '',
    exam_id: ''
  });
  const [students, setStudents] = useState<Student[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && user?.school_id) {
      fetchData();
    }
  }, [open, user?.school_id]);

  const fetchData = async () => {
    try {
      console.log('AddGradeDialog: Fetching data for school_id:', user?.school_id);
      const [studentsData, coursesData, examsData] = await Promise.all([
        getStudents(user?.school_id),
        getCourses(user?.school_id),
        getExams(undefined, user?.school_id)
      ]);
      console.log('AddGradeDialog: Students loaded:', studentsData);
      console.log('AddGradeDialog: Courses loaded:', coursesData);
      console.log('AddGradeDialog: Exams loaded:', examsData);
      setStudents(studentsData);
      setCourses(coursesData);
      setExams(examsData);
    } catch (err) {
      console.error('AddGradeDialog: Error loading data:', err);
      setError('Failed to load data');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await createGrade({
        ...formData,
        student_id: parseInt(formData.student_id),
        course_id: parseInt(formData.course_id),
        exam_id: formData.exam_id ? parseInt(formData.exam_id) : undefined,
        grade: parseFloat(formData.grade),
        max_grade: parseFloat(formData.max_grade)
      });
      onSuccess();
      onClose();
      setFormData({
        student_id: '',
        course_id: '',
        assignment_name: '',
        grade: '',
        max_grade: '',
        grade_type: 'assignment',
        comments: '',
        exam_id: ''
      });
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create grade');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string) => (event: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Grade</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 300px' }}>
              <FormControl fullWidth required>
                <InputLabel>Student</InputLabel>
                <Select
                  value={formData.student_id}
                  onChange={handleChange('student_id')}
                  label="Student"
                >
                  {students.map(student => (
                    <MenuItem key={student.id} value={student.id}>
                      {student.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: '1 1 300px' }}>
              <FormControl fullWidth required>
                <InputLabel>Course</InputLabel>
                <Select
                  value={formData.course_id}
                  onChange={handleChange('course_id')}
                  label="Course"
                >
                  {courses.map(course => (
                    <MenuItem key={course.id} value={course.id}>
                      {course.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: '1 1 300px' }}>
              <FormControl fullWidth>
                <InputLabel>Exam (Optional)</InputLabel>
                <Select
                  value={formData.exam_id || ''}
                  onChange={handleChange('exam_id')}
                  label="Exam (Optional)"
                >
                  <MenuItem value="">No Exam</MenuItem>
                  {exams.map(exam => (
                    <MenuItem key={exam.id} value={exam.id}>
                      {exam.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                label="Assignment Name"
                value={formData.assignment_name}
                onChange={handleChange('assignment_name')}
                required
              />
            </Box>

            <Box sx={{ flex: '1 1 200px' }}>
              <TextField
                fullWidth
                label="Grade"
                type="number"
                value={formData.grade}
                onChange={handleChange('grade')}
                required
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Box>

            <Box sx={{ flex: '1 1 200px' }}>
              <TextField
                fullWidth
                label="Maximum Grade"
                type="number"
                value={formData.max_grade}
                onChange={handleChange('max_grade')}
                required
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Box>

            <Box sx={{ flex: '1 1 200px' }}>
              <FormControl fullWidth required>
                <InputLabel>Grade Type</InputLabel>
                <Select
                  value={formData.grade_type}
                  onChange={handleChange('grade_type')}
                  label="Grade Type"
                >
                  <MenuItem value="assignment">Assignment</MenuItem>
                  <MenuItem value="quiz">Quiz</MenuItem>
                  <MenuItem value="exam">Exam</MenuItem>
                  <MenuItem value="project">Project</MenuItem>
                  <MenuItem value="participation">Participation</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: '1 1 100%' }}>
              <TextField
                fullWidth
                label="Comments"
                multiline
                rows={3}
                value={formData.comments}
                onChange={handleChange('comments')}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Creating...' : 'Create Grade'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddGradeDialog;
